import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft, faSpinner, faCheckCircle, faTimesCircle,
    faChevronRight, faInbox, faCheck, faTimes
} from '@fortawesome/free-solid-svg-icons';
import { db, functions, storage } from '../firebase';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ref, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../context/AuthContext';
import './JoinRequestsPage.css';

function JoinRequestsPage() {
    const { userOrgId } = useAuth();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('pending');
    const [expandedId, setExpandedId] = useState(null);
    const [processing, setProcessing] = useState(null); // requestId being processed
    const [photoUrls, setPhotoUrls] = useState({});

    // Real-time listener for join requests
    useEffect(() => {
        if (!userOrgId) return;

        const q = query(
            collection(db, 'joinRequests'),
            where('orgId', '==', userOrgId),
            where('status', '==', activeTab),
            orderBy('cDate', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const items = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
            }));
            setRequests(items);
            setLoading(false);
        }, (error) => {
            console.error('Error listening to join requests:', error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [userOrgId, activeTab]);

    // Load photo URLs for expanded requests
    useEffect(() => {
        if (!expandedId) return;
        const request = requests.find(r => r.id === expandedId);
        if (!request?.familyPhoto || photoUrls[expandedId]) return;

        const loadPhoto = async () => {
            try {
                const photoRef = ref(storage, request.familyPhoto);
                const url = await getDownloadURL(photoRef);
                setPhotoUrls(prev => ({ ...prev, [expandedId]: url }));
            } catch (e) {
                console.warn('Could not load photo for request:', expandedId);
            }
        };
        loadPhoto();
    }, [expandedId, requests, photoUrls]);

    const handleReview = async (requestId, action) => {
        setProcessing(requestId);
        try {
            const reviewFn = httpsCallable(functions, 'reviewJoinRequest');
            await reviewFn({ requestId, action });
            setExpandedId(null);
        } catch (error) {
            console.error('Error reviewing request:', error);
            alert(`Error: ${error.message || 'Failed to process request'}`);
        } finally {
            setProcessing(null);
        }
    };

    const toggleExpand = (id) => {
        setExpandedId(expandedId === id ? null : id);
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: 'numeric', minute: '2-digit'
        });
    };

    const formatAddress = (family) => {
        return [family.street, family.city, family.state, family.zipcode]
            .filter(Boolean)
            .join(', ');
    };

    return (
        <div className="join-requests-page">
            {/* Header */}
            <div className="join-requests-header">
                <div className="container">
                    <div className="join-requests-header-content">
                        <div className="join-requests-header-left">
                            <Link to="/dashboard" className="join-requests-back-btn">
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </Link>
                            <h1>Join Requests</h1>
                        </div>
                    </div>
                </div>
            </div>

            <div className="join-requests-container">
                {/* Filter Tabs */}
                <div className="join-requests-tabs">
                    {['pending', 'approved', 'rejected'].map(tab => (
                        <button
                            key={tab}
                            className={`join-requests-tab ${activeTab === tab ? 'active' : ''}`}
                            onClick={() => { setActiveTab(tab); setExpandedId(null); setLoading(true); }}
                        >
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </button>
                    ))}
                </div>

                {/* Loading */}
                {loading && (
                    <div className="join-requests-empty">
                        <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2rem', color: '#1565c0' }} />
                        <p>Loading...</p>
                    </div>
                )}

                {/* Empty State */}
                {!loading && requests.length === 0 && (
                    <div className="join-requests-empty">
                        <FontAwesomeIcon icon={faInbox} />
                        <p>No {activeTab} join requests</p>
                    </div>
                )}

                {/* Request List */}
                {!loading && requests.map(request => {
                    const family = request.family || {};
                    const members = request.members || [];
                    const isExpanded = expandedId === request.id;
                    const isProcessing = processing === request.id;

                    return (
                        <div
                            key={request.id}
                            className={`join-request-item ${isExpanded ? 'expanded' : ''}`}
                        >
                            {/* Summary Row */}
                            <div
                                className="join-request-summary"
                                onClick={() => toggleExpand(request.id)}
                            >
                                <div className="join-request-avatar">
                                    {(family.surname || '?')[0].toUpperCase()}
                                </div>
                                <div className="join-request-info">
                                    <h3>{family.surname || 'Unknown'}</h3>
                                    <p>
                                        {members.length} member{members.length !== 1 ? 's' : ''}
                                        {request.cDate && ` · ${formatDate(request.cDate)}`}
                                    </p>
                                </div>
                                <span className={`join-request-status ${request.status}`}>
                                    {request.status}
                                </span>
                                <FontAwesomeIcon
                                    icon={faChevronRight}
                                    className="join-request-chevron"
                                />
                            </div>

                            {/* Expanded Detail */}
                            {isExpanded && (
                                <div className="join-request-detail">
                                    {/* Photo */}
                                    {request.familyPhoto && photoUrls[request.id] && (
                                        <div className="join-request-detail-section">
                                            <img
                                                src={photoUrls[request.id]}
                                                alt="Family"
                                                className="join-request-photo"
                                            />
                                        </div>
                                    )}

                                    {/* Family Info */}
                                    <div className="join-request-detail-section">
                                        <h4>Family</h4>
                                        <div className="join-request-detail-row">
                                            <span className="join-request-detail-label">Surname</span>
                                            <span className="join-request-detail-value">{family.surname}</span>
                                        </div>
                                        {family.phone && (
                                            <div className="join-request-detail-row">
                                                <span className="join-request-detail-label">Phone</span>
                                                <span className="join-request-detail-value">{family.phone}</span>
                                            </div>
                                        )}
                                        {formatAddress(family) && (
                                            <div className="join-request-detail-row">
                                                <span className="join-request-detail-label">Address</span>
                                                <span className="join-request-detail-value">{formatAddress(family)}</span>
                                            </div>
                                        )}
                                        {family.anniversaryMonth && (
                                            <div className="join-request-detail-row">
                                                <span className="join-request-detail-label">Anniversary</span>
                                                <span className="join-request-detail-value">
                                                    {family.anniversaryMonth}/{family.anniversaryDay}
                                                    {family.anniversaryYear ? `/${family.anniversaryYear}` : ''}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Members */}
                                    <div className="join-request-detail-section">
                                        <h4>Members ({members.length})</h4>
                                        {members.map((member, idx) => (
                                            <div key={idx} className="join-request-member-card">
                                                <h5>{member.name || 'Unknown'}</h5>
                                                {member.email && (
                                                    <div className="join-request-detail-row">
                                                        <span className="join-request-detail-label">Email</span>
                                                        <span className="join-request-detail-value">{member.email}</span>
                                                    </div>
                                                )}
                                                {member.cellphone && (
                                                    <div className="join-request-detail-row">
                                                        <span className="join-request-detail-label">Phone</span>
                                                        <span className="join-request-detail-value">{member.cellphone}</span>
                                                    </div>
                                                )}
                                                {member.birthdayMonth && (
                                                    <div className="join-request-detail-row">
                                                        <span className="join-request-detail-label">Birthday</span>
                                                        <span className="join-request-detail-value">
                                                            {member.birthdayMonth}/{member.birthdayDay}
                                                            {member.birthdayYear ? `/${member.birthdayYear}` : ''}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    {/* Actions (pending only) */}
                                    {request.status === 'pending' && (
                                        <div className="join-request-actions">
                                            <button
                                                className="join-request-reject-btn"
                                                onClick={() => handleReview(request.id, 'reject')}
                                                disabled={isProcessing}
                                            >
                                                {isProcessing ? (
                                                    <FontAwesomeIcon icon={faSpinner} spin />
                                                ) : (
                                                    <FontAwesomeIcon icon={faTimes} />
                                                )}
                                                Reject
                                            </button>
                                            <button
                                                className="join-request-approve-btn"
                                                onClick={() => handleReview(request.id, 'approve')}
                                                disabled={isProcessing}
                                            >
                                                {isProcessing ? (
                                                    <FontAwesomeIcon icon={faSpinner} spin />
                                                ) : (
                                                    <FontAwesomeIcon icon={faCheck} />
                                                )}
                                                Approve
                                            </button>
                                        </div>
                                    )}

                                    {/* Review info (approved/rejected) */}
                                    {request.status !== 'pending' && request.reviewedAt && (
                                        <div className="join-request-detail-section" style={{ marginTop: '0.5rem' }}>
                                            <p style={{ fontSize: '0.85rem', color: '#5c5c5c', margin: 0 }}>
                                                {request.status === 'approved' ? (
                                                    <><FontAwesomeIcon icon={faCheckCircle} style={{ color: '#2e7d32' }} /> Approved</>
                                                ) : (
                                                    <><FontAwesomeIcon icon={faTimesCircle} style={{ color: '#c62828' }} /> Rejected</>
                                                )}
                                                {' on '}{formatDate(request.reviewedAt)}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default JoinRequestsPage;
